import { DonorAuthErrorCode } from './donor-auth-provider';
import { codeChallengeS256, randomUrlSafe } from './pkce';
import { isPortalPath, portalPath } from '../../portalPaths';

/**
 * Donor sign-in through the Cognito Hosted UI: authorization code + PKCE.
 *
 * The donor is sent to the pool's own sign-in page and comes back with a
 * one-time code, which this file exchanges for tokens. Our code never sees a
 * password -- not on a form, not in a request body.
 *
 * Why the Hosted UI rather than our own form, on a shared pool:
 *
 *   1. A donor who signed up through Google has no Cognito password, and none
 *      can be set on a federated user. An email+password form is a permanent
 *      dead end for them. Social sign-in is only reachable through Cognito's
 *      OAuth endpoints.
 *   2. Cognito keeps a session cookie on the hosted domain, so a donor already
 *      signed in to another Masjidal product comes straight back with a code:
 *      real single sign-on.
 *
 * The hosted page also owns everything that is easy to get wrong about a login
 * form: lockout, password reset, the first-login password change for accounts
 * created with a temporary password, and MFA if it is ever switched on.
 *
 * Configuration is two values: the app client id and the Hosted UI domain. The
 * redirect and sign-out URIs are derived from window.location.origin, so they
 * follow whichever host serves the UI -- each one must be allowlisted on the app
 * client. See docs/donor-portal/cognito-requirements.md.
 *
 * Token endpoints are called with native fetch, deliberately not the donorApi
 * axios instance: that one attaches Bearer tokens and the API base URL.
 */

const STORAGE_PREFIX = 'donorPortal.';
const TOKENS_KEY = `${STORAGE_PREFIX}tokens`;
const PENDING_KEY = `${STORAGE_PREFIX}pendingSignIn`;
const RESET_INTENT_KEY = `${STORAGE_PREFIX}resetPasswordAfterSignOut`;

// Functions, not constants: on the donor domain the portal lives at the root,
// elsewhere under /donor-portal (see portalPaths.js). Both URLs below must be
// allowlisted on the app client for every origin the portal is served from.
const PORTAL_ROOT = () => portalPath();
const CALLBACK_PATH = () => portalPath('auth/callback');
const SIGNED_OUT_PATH = () => portalPath('login');

/**
 * openid + email is all the API needs: it identifies a donor by the verified
 * email in the ID token, and the donor's name comes from our own database via
 * /donor-portal/me. Asking for less means fewer scopes the app client has to
 * allow, and fewer ways for the authorize request to be refused.
 */
const SCOPES = 'openid email';

/** A sign-in left half-finished longer than this has to start again. */
const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

/** "Reset my password next" survives the trip through /logout for this long. */
const RESET_INTENT_MAX_AGE_MS = 10 * 60 * 1000;

/** Refresh this long before expiry, so a token never dies mid-request. */
const EXPIRY_SKEW_MS = 60 * 1000;

/** Sign-out waits at most this long for revocation before leaving anyway. */
const REVOKE_TIMEOUT_MS = 3000;

const config = () => {
    const clientId = (process.env.REACT_APP_DONOR_COGNITO_CLIENT_ID || '').trim();
    const rawDomain = (process.env.REACT_APP_DONOR_COGNITO_DOMAIN || '').trim();
    // Accepted with or without a scheme. Cognito domains are always https.
    const host = rawDomain.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
    return { clientId, domain: host ? `https://${host}` : '' };
};

const redirectUri = () => `${window.location.origin}${CALLBACK_PATH()}`;
const signOutUri = () => `${window.location.origin}${SIGNED_OUT_PATH()}`;

const authError = (code, message) => ({ code, message });

const NOT_CONFIGURED = () =>
    authError(
        DonorAuthErrorCode.NOT_CONFIGURED,
        'The donor portal is not configured. Please contact support.',
    );

/**
 * Tokens live in sessionStorage, not localStorage.
 *
 * The Donor Portal shares an origin with the admin app and the public donation
 * pages, so any XSS anywhere on this domain can read whatever is in web storage.
 * Neither option defends against that. sessionStorage is chosen because it
 * narrows the window: tokens do not persist after the tab closes, which matters
 * because donors realistically check their history from shared and family
 * computers, and a long-lived refresh token left behind on one of those is a
 * worse outcome than asking them to sign in again.
 *
 * It is also what carries the PKCE verifier and state across the redirect: the
 * hosted page is a top-level navigation in the same tab, and sessionStorage
 * survives it.
 */
const readJson = (key) => {
    try {
        const raw = window.sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writeJson = (key, value) => {
    try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        /* private mode or storage disabled; the session simply will not persist */
    }
};

const removeKey = (key) => {
    try {
        window.sessionStorage.removeItem(key);
    } catch (e) {
        /* nothing to clean up */
    }
};

const clearDonorStorage = () => {
    try {
        Object.keys(window.sessionStorage)
            .filter((k) => k.startsWith(STORAGE_PREFIX))
            .forEach((k) => window.sessionStorage.removeItem(k));
    } catch (e) {
        /* nothing to clean up */
    }
};

/**
 * Only a path inside the portal, and never back into the auth pages. Anything
 * else -- an absolute URL, a protocol-relative "//host", a backslash trick --
 * falls back to the portal root, so the callback can never be turned into an
 * open redirect.
 */
const safeReturnTo = (value) => {
    const root = PORTAL_ROOT();
    if (typeof value !== 'string') return root;
    if (value.startsWith('//') || value.includes('\\')) return root;
    if (!isPortalPath(value)) return root;

    if (value.startsWith(CALLBACK_PATH()) || value.startsWith(SIGNED_OUT_PATH())) return root;
    return value;
};

/**
 * Reads a JWT's claims WITHOUT verifying it.
 *
 * Used for two things only: knowing when to refresh, and showing the email on
 * first paint. Nothing here is an authorization decision -- the API verifies the
 * signature, issuer, audience, expiry and email_verified on every request, and a
 * forged token would simply be refused there.
 */
const decodeJwtPayload = (jwt) => {
    try {
        const segment = String(jwt).split('.')[1];
        if (!segment) return null;
        const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const binary = window.atob(padded);
        const percentEncoded = Array.prototype.map
            .call(binary, (ch) => `%${`00${ch.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join('');
        return JSON.parse(decodeURIComponent(percentEncoded));
    } catch (e) {
        return null;
    }
};

const postForm = async (url, params) => {
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(params).toString(),
        // No ambient credentials: the token endpoints need none, and the
        // donor portal never relies on cookies.
        credentials: 'omit',
    });

    let body = null;
    try {
        body = await response.json();
    } catch (e) {
        body = null; // /oauth2/revoke answers 200 with an empty body
    }

    if (!response.ok) {
        const error = new Error(body?.error_description || body?.error || `HTTP ${response.status}`);
        error.oauthError = body?.error;
        throw error;
    }
    return body;
};

const withTimeout = (promise, ms) => {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('timed out')), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const storeTokens = (body, previousRefreshToken) => {
    const idToken = body?.id_token;
    if (!idToken) throw new Error('The token response carried no ID token.');

    // Expiry of the token we actually send -- the ID token's own `exp`.
    const claims = decodeJwtPayload(idToken);
    const expiresAt = claims?.exp
        ? claims.exp * 1000
        : Date.now() + (Number(body.expires_in) || 0) * 1000;

    const tokens = {
        idToken,
        accessToken: body.access_token || null,
        // Cognito does not rotate refresh tokens by default, so a refresh
        // response usually omits one. Keep the one we have.
        refreshToken: body.refresh_token || previousRefreshToken || null,
        expiresAt,
    };
    writeJson(TOKENS_KEY, tokens);
    return tokens;
};

export const isConfigured = () => {
    const { clientId, domain } = config();
    return Boolean(clientId && domain);
};

/** Idempotent. Nothing to set up beyond configuration, which is read lazily. */
export const configure = () => (isConfigured() ? config() : null);

/**
 * Sends the donor to the hosted sign-in page. Does not return in practice: the
 * browser navigates away, and the flow resumes at /donor-portal/auth/callback.
 *
 * `replace: true` replaces the current history entry instead of adding one. Use
 * it for automatic redirects, so Back on the hosted page does not return to a
 * page that would redirect forward again. A click on a button keeps the default,
 * so Back returns to that button.
 */
export const signIn = (returnTo, options) => startHostedFlow('oauth2/authorize', returnTo, options);

/**
 * Change password, through Cognito's hosted pages: they have no change-password
 * page, only "forgot password" (Cognito emails a code, the donor chooses a new
 * password there, then signs in and comes back through /auth/callback like any
 * sign-in). Our code never sees either password.
 *
 * Cognito's forgot-password page refuses the reset -- "Invalid request: Please
 * check your input and try again" -- while the donor is still signed in to
 * Cognito, which a donor pressing this button always is. So this signs them out
 * of Cognito first (the same /logout as Sign out) and leaves a note; the login
 * page that /logout returns to sees it and carries straight on to the reset
 * page (resumePasswordReset). One click for the donor, and Cognito sees a
 * signed-out reset, which works.
 */
export const changePassword = (returnTo) => signOut({ thenResetPassword: returnTo ?? null });

/** True on the login page when it should carry on to the reset page. */
export const hasPendingPasswordReset = () => {
    const intent = readJson(RESET_INTENT_KEY);
    return !!intent && Date.now() - Number(intent.at) <= RESET_INTENT_MAX_AGE_MS;
};

/**
 * Opens the hosted reset page if changePassword sent the donor here. The note is
 * used once: it is removed before anything else, so a reload, Back or a second
 * call can never repeat the redirect. Resolves true when it is navigating away.
 */
export const resumePasswordReset = async () => {
    const intent = readJson(RESET_INTENT_KEY);
    removeKey(RESET_INTENT_KEY);
    if (!intent || Date.now() - Number(intent.at) > RESET_INTENT_MAX_AGE_MS) return false;

    // replace: Back on the reset page should not land on a page that redirects
    // forward again.
    await startHostedFlow('forgotPassword', intent.returnTo, { replace: true });
    return true;
};

const startHostedFlow = async (hostedPath, returnTo, { replace = false } = {}) => {
    // A sign-out is on its way to /logout; starting a sign-in now would cancel
    // it (see signingOut). Checked again after the await below, since a
    // sign-out can begin while the challenge is being computed.
    if (signingOut) return;
    if (!isConfigured()) throw NOT_CONFIGURED();

    const { clientId, domain } = config();
    const verifier = randomUrlSafe(32);
    const state = randomUrlSafe(16);
    const challenge = await codeChallengeS256(verifier);
    if (signingOut) return;

    writeJson(PENDING_KEY, {
        verifier,
        state,
        returnTo: safeReturnTo(returnTo),
        createdAt: Date.now(),
    });

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri(),
        scope: SCOPES,
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
    });

    // URLSearchParams writes spaces as "+". Spell the scope separator as %20
    // instead; every other value is already percent-encoded, so this touches
    // nothing else.
    const url = `${domain}/${hostedPath}?${params.toString().replace(/\+/g, '%20')}`;
    if (replace) {
        window.location.replace(url);
    } else {
        window.location.assign(url);
    }
};

const completeSignIn = async (params) => {
    if (!isConfigured()) throw NOT_CONFIGURED();

    const error = params.get('error');
    const code = params.get('code');
    const state = params.get('state');
    const pending = readJson(PENDING_KEY);

    if (error) {
        removeKey(PENDING_KEY);
        if (error === 'access_denied') {
            throw authError(DonorAuthErrorCode.CANCELLED, 'Sign-in was cancelled.');
        }
        throw authError(
            DonorAuthErrorCode.CALLBACK_INVALID,
            'Sign-in could not be completed. Please try again.',
        );
    }

    // Each of these is refused before any network call: a callback that did not
    // start in this tab, from this sign-in, is not one we will redeem.
    const stale = !pending || Date.now() - Number(pending.createdAt) > PENDING_MAX_AGE_MS;
    if (!code || !state || stale || pending.state !== state) {
        removeKey(PENDING_KEY);
        throw authError(
            DonorAuthErrorCode.CALLBACK_INVALID,
            'This sign-in link is no longer valid. Please sign in again.',
        );
    }

    // Single-use. Cleared before the exchange, so it cannot be replayed even if
    // the exchange itself fails.
    removeKey(PENDING_KEY);

    const { clientId, domain } = config();
    let body;
    try {
        body = await postForm(`${domain}/oauth2/token`, {
            grant_type: 'authorization_code',
            client_id: clientId,
            code,
            redirect_uri: redirectUri(),
            code_verifier: pending.verifier,
        });
    } catch (e) {
        throw authError(
            DonorAuthErrorCode.CALLBACK_INVALID,
            'Sign-in could not be completed. Please try again.',
        );
    }

    storeTokens(body, null);
    return safeReturnTo(pending.returnTo);
};

/**
 * Authorization codes are single-use: a second exchange of the same code fails
 * with invalid_grant. Memoising per code makes a repeated call -- a remount, a
 * double effect -- resolve to the first result instead of failing.
 */
const exchanges = new Map();

/** Finishes sign-in on the callback route. Resolves to where to send the donor. */
export const handleRedirectCallback = (search) => {
    const params = new URLSearchParams(search || '');
    const code = params.get('code');
    if (!code) return completeSignIn(params);

    if (!exchanges.has(code)) exchanges.set(code, completeSignIn(params));
    return exchanges.get(code);
};

let refreshInFlight = null;

const refresh = async (refreshToken) => {
    const { clientId, domain } = config();
    try {
        const body = await postForm(`${domain}/oauth2/token`, {
            grant_type: 'refresh_token',
            client_id: clientId,
            refresh_token: refreshToken,
        });
        return storeTokens(body, refreshToken).idToken;
    } catch (e) {
        // Only a refusal from Cognito ends the session. A network blip returns
        // null for this request but keeps the refresh token for the next one.
        if (e?.oauthError === 'invalid_grant') removeKey(TOKENS_KEY);
        return null;
    }
};

/**
 * A currently-valid ID token, or null.
 *
 * The ID token, not the access token: the API identifies a donor by the verified
 * email, which only the ID token carries. The name is kept for the contract.
 *
 * Refreshes transparently near expiry. The dashboard fires several requests at
 * once on load, so concurrent callers share one refresh rather than racing to
 * spend the same refresh token. Never cache the result.
 */
export const getAccessToken = async () => {
    if (!isConfigured()) return null;

    const tokens = readJson(TOKENS_KEY);
    if (!tokens?.idToken) return null;

    if (Number(tokens.expiresAt) - Date.now() > EXPIRY_SKEW_MS) return tokens.idToken;

    if (!tokens.refreshToken) {
        removeKey(TOKENS_KEY);
        return null;
    }

    if (!refreshInFlight) {
        refreshInFlight = refresh(tokens.refreshToken).finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

/**
 * Synchronous, for first paint only. Never a source of authorization.
 *
 * `provider` is set when the donor signed in through a social provider ("Google"),
 * from the ID token's `identities` claim. Such a donor has no Cognito password,
 * so there is none to change.
 */
export const getSessionUser = () => {
    const tokens = readJson(TOKENS_KEY);
    const claims = tokens?.idToken ? decodeJwtPayload(tokens.idToken) : null;
    if (!claims?.email) return null;
    const provider = Array.isArray(claims.identities) ? claims.identities[0]?.providerName : null;
    return { email: claims.email, provider: provider || null };
};

/**
 * Signs out, properly.
 *
 *   1. Local tokens are cleared first, so nothing below can leave them behind.
 *   2. Our refresh token is revoked at Cognito. Scoped to this app client --
 *      the donor's sessions in other Masjidal products are untouched, unlike
 *      globalSignOut.
 *   3. The browser goes to the hosted /logout, which clears the hosted page's
 *      session cookie. Without this, "Sign in" straight after "Sign out" would
 *      log the donor back in with no password -- on a shared computer, the next
 *      person would be in their account. It does mean their next fresh sign-in
 *      to another Masjidal product through the hosted page asks for credentials;
 *      that product's current session keeps working.
 *
 * /logout requires our sign-out URI to be allowlisted on the app client.
 */
/**
 * Set for the rest of this page's life once sign-out starts; the page then
 * unloads into /logout, which resets it.
 *
 * Signing out clears our storage, so to the rest of the app the donor looks
 * signed out *before* the browser has left for /logout. Anything that reacted
 * by starting a sign-in -- the route guard's automatic redirect, above all --
 * would replace that pending navigation. /logout would never run, Cognito's
 * session would survive, and the donor would be signed straight back in. So
 * once this is set, signIn does nothing.
 */
let signingOut = false;

/**
 * Returns true when the browser is on its way to Cognito's /logout, so the
 * caller knows not to update anything: the page is about to unload.
 *
 * thenResetPassword (from changePassword): after /logout, the login page opens
 * the hosted reset page, then returns the donor to this path once signed in.
 */
export const signOut = async ({ thenResetPassword } = {}) => {
    signingOut = true;
    const tokens = readJson(TOKENS_KEY);
    clearDonorStorage();

    if (!isConfigured()) {
        signingOut = false;
        return false;
    }

    // Written after clearDonorStorage, which would otherwise remove it.
    if (thenResetPassword !== undefined) {
        writeJson(RESET_INTENT_KEY, {
            returnTo: safeReturnTo(thenResetPassword ?? undefined),
            at: Date.now(),
        });
    }
    const { clientId, domain } = config();

    if (tokens?.refreshToken) {
        await withTimeout(
            postForm(`${domain}/oauth2/revoke`, { token: tokens.refreshToken, client_id: clientId }),
            REVOKE_TIMEOUT_MS,
        ).catch(() => {
            /* best effort; local state is already gone */
        });
    }

    const params = new URLSearchParams({ client_id: clientId, logout_uri: signOutUri() });
    window.location.assign(`${domain}/logout?${params.toString()}`);
    return true;
};

/** Test-only: a test environment never unloads the page to reset the flag. */
export const resetSigningOutForTests = () => {
    signingOut = false;
};
